import React from 'react';
import PropTypes from 'prop-types';
import {formatUrl} from 'url-lib';
import includes from 'lodash/includes';

import {
    GRID_TYPE,
    STANDARD_STYLE,
    IMAGE_STYLE_FIXED,
    TYPES_PROP_TYPE,
    STYLES_PROP_TYPE,
    IMAGE_STYLES_PROP_TYPE,
} from 'eventbrite_design_system/mediaCardContent/constants';

import EventCard from 'eventbrite_design_system/eventCard/EnhancedEventCard';
import {
    EVENTBRITE,
    SHARE_OPTIONS_PROPTYPE,
    EVENT_PROPTYPES,
    CITY_BROWSE_SECTION,
    SEARCH_SECTION,
    EVENT_AFFILIATES_MAP,
    GA_VIEW_EVENT_ACTION,
    GA_EVENT_CARD_CATEGORY_MAP,
    GA_TERM_MAP,
    GA_CATEGORY_MAP,
    GA_SHARE_ATTEMPT_ACTION,
    GA_SHARE_ACTION,
    GA_EXTERNAL_VIEW_EVENT_ACTION,
    GA_CREATE_COLLECTION_VIEW_ACTION,
    GA_ADD_TO_COLLECTION_SHOW_ACTION,
    SHARE_TYPE_GA_MAP,
    LINK_FOLLOW_WHITELIST,
    EXPERIMENTS_SHAPE_PROP_TYPE,
} from '../constants';

import {getLocationInfo} from '../utils/venue';


const _createShareUrl = (url, section) => formatUrl(
    url,
    {
        'utm-medium': 'discovery',
        'utm-campaign': 'social',
        'utm-content': 'attendeeshare',
        'utm-source': 'cp',
        'utm-term': GA_TERM_MAP[section],
    }
);


export default class SearchEventCard extends React.PureComponent {
    static propTypes = {
        ...EVENT_PROPTYPES,
        edgeColor: PropTypes.string,
        neighborhood: PropTypes.string,
        city: PropTypes.string,
        onSaveEvent: PropTypes.func.isRequired,
        shareOptions: SHARE_OPTIONS_PROPTYPE.isRequired,
        isAuthenticated: PropTypes.bool,
        type: TYPES_PROP_TYPE,
        style: STYLES_PROP_TYPE,
        imageStyle: IMAGE_STYLES_PROP_TYPE,
        containerSpacingClasses: PropTypes.string,
        trackClickInternal: PropTypes.func,
        section: PropTypes.oneOf([CITY_BROWSE_SECTION, SEARCH_SECTION]),
        excludeExtraContent: PropTypes.bool,
        experiments: EXPERIMENTS_SHAPE_PROP_TYPE,
        trackGAEvent: PropTypes.func,
        userCollections: PropTypes.arrayOf(PropTypes.object),
        eventCollections: PropTypes.arrayOf(PropTypes.string),
        onAddOrRemoveEventToCollection: PropTypes.func,
        onCreateCollection: PropTypes.func,
    };

    static defaultProps = {
        style: STANDARD_STYLE,
        type: GRID_TYPE,
        imageStyle: IMAGE_STYLE_FIXED,
        // eslint-disable-next-line react/default-props-match-prop-types
        venueName: '',
        // eslint-disable-next-line react/default-props-match-prop-types
        venueAddress: '',
        section: CITY_BROWSE_SECTION,
    }

    /* Callback on the onClick of a share option within the callback to pass
    to ga */
    _handleShareOptionClick = (shareType) => {
        const {section, trackGAEvent} = this.props;

        if (typeof trackGAEvent === 'function') {
            trackGAEvent({
                category: GA_EVENT_CARD_CATEGORY_MAP[section],
                action: GA_SHARE_ACTION,
                label: SHARE_TYPE_GA_MAP[shareType],
            });
        }
    }

    _handleShareClick = () => {
        const {
            id: eventId,
            section,
            trackGAEvent,
        } = this.props;

        if (typeof trackGAEvent === 'function') {
            trackGAEvent({
                category: GA_CATEGORY_MAP[section],
                action: GA_SHARE_ATTEMPT_ACTION,
                label: eventId,
            });
        }
    }

    _handleEventClick = () => {
        const {
            id,
            trackClick,
            ticketsBy,
            trackClickInternal,
        } = this.props;

        if (trackClick) {
            const actionType = ticketsBy === EVENTBRITE ? GA_VIEW_EVENT_ACTION : GA_EXTERNAL_VIEW_EVENT_ACTION;

            trackClick(actionType);
        }

        if (trackClickInternal) {
            trackClickInternal(id, ticketsBy);
        }
    }

    _handleShowCreateCollectionView = () => {
        const {trackGAEvent, section, userCollections} = this.props;

        if (typeof trackGAEvent === 'function') {
            trackGAEvent({
                category: GA_CATEGORY_MAP[section],
                action: GA_CREATE_COLLECTION_VIEW_ACTION,
                label: userCollections ? `${userCollections.length}` : 0,
            });
        }
    }

    _handleShowAddToCollectionView = () => {
        const {trackGAEvent, section, userCollections} = this.props;

        if (typeof trackGAEvent === 'function') {
            trackGAEvent({
                category: GA_CATEGORY_MAP[section],
                action: GA_ADD_TO_COLLECTION_SHOW_ACTION,
                label: userCollections ? `${userCollections.length}` : 0,
            });
        }
    }


    render() {
        let {
            id,
            onSaveEvent,
            shareOptions,
            imageUrl,
            edgeColor,
            name,
            startDate,
            startTime,
            venueName,
            venueAddress,
            minPrice,
            ticketsBy,
            url,
            savedByYou,
            isAuthenticated,
            type,
            style,
            imageStyle,
            containerSpacingClasses,
            section,
            excludeExtraContent,
            isFree,
            isSoldOut,
            seriesUrl,
            repeatingInstanceCount,
            experiments,
            focused,
            userCollections,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            eventCollections,
        } = this.props;

        const eventUrl = _createShareUrl(url, section);

        const updatedUTMOptions = {
            ...shareOptions,
            eventUrl,
            eventName: name,
            eventId: id,
            onClick: this._handleShareOptionClick.bind(this),
            utmOptions: {
                'utm-term': GA_TERM_MAP[section],
            },
            trackingCode: EVENT_AFFILIATES_MAP[section],
        };

        const locationInfo = getLocationInfo(venueName, venueAddress);
        const EventCardComponent = EventCard;

        let priceInfo;

        /* We want to default the cards on search pages to not include the
        extra content unless explicitly passed through */
        if (section === SEARCH_SECTION) {
            // On search event cards we want price information
            priceInfo = minPrice;
            excludeExtraContent = true;
        }

        // Since we currently noindex children events,
        // we link their event cards to the parent event page (which is indexed)
        // so that parent events can be crawled.
        // See https://jira.evbhome.com/browse/EB-109015
        if (seriesUrl) {
            url = seriesUrl;
        }

        return (
            <div className="search-event-card eds-l-mar-vert-1">
                <EventCardComponent
                    id={id}
                    onSaveEvent={onSaveEvent}
                    shareOptions={updatedUTMOptions}
                    onClick={this._handleEventClick}
                    onWillShareClick={this._handleShareClick}
                    onDidShareClick={this._handleShareOptionClick}
                    imageUrl={imageUrl}
                    edgeColor={edgeColor}
                    name={name}
                    startDate={startDate}
                    startTime={startTime}
                    locationInfo={locationInfo}
                    minPrice={priceInfo}
                    ticketsBy={ticketsBy}
                    url={url}
                    savedByYou={savedByYou}
                    isAuthenticated={isAuthenticated}
                    type={type}
                    style={style}
                    imageStyle={imageStyle}
                    containerSpacingClasses={containerSpacingClasses}
                    isFree={isFree}
                    isSoldOut={isSoldOut}
                    isConsumer={true}
                    excludeDateThumbnail={!!excludeExtraContent}
                    repeatingInstanceCount={repeatingInstanceCount}
                    isNoFollow={!includes(LINK_FOLLOW_WHITELIST, ticketsBy)}
                    experiments={experiments}
                    focused={focused}
                    userCollections={userCollections}
                    onShowCreateCollectionView={this._handleShowCreateCollectionView}
                    onShowAddToCollectionView={this._handleShowAddToCollectionView}
                    onAddOrRemoveEventToCollection={onAddOrRemoveEventToCollection}
                    onCreateCollection={onCreateCollection}
                    eventCollections={eventCollections}
                />
            </div>
        );
    }
}
